/**
 * ML Prediction Insights Panel
 * Detailed panel showing churn prediction with contributing factors and recommendations
 *
 * MIGRATED: MUI -> Tailwind CSS components
 * Trust Marketplace - ML Prediction System
 */

import React, { useState, useEffect } from 'react';
import {
  Brain,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  Info,
  Phone,
  Home,
  MessageSquare,
  Mail,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { authFetch } from '@/lib/authFetch';

interface ContributingFactor {
  factor: string;
  value: number | string;
  importance: number;
  impact: 'high' | 'medium' | 'low';
}

interface MLPrediction {
  member_id: number;
  enrollment_id?: number;
  churn_probability: number;
  will_churn_predicted: boolean;
  risk_level: 'low' | 'moderate' | 'high' | 'critical';
  prediction_horizon_days: number;
  model_version: string;
  contributing_factors: ContributingFactor[];
  predicted_at: string;
}

interface RecommendedAction {
  action: string;
  priority: 'high' | 'medium' | 'low';
  icon: React.ReactNode;
  reason: string;
}

interface MLPredictionInsightsPanelProps {
  memberId: number;
  enrollmentId?: number;
  compact?: boolean;
  onRefresh?: () => void;
}

// Generate recommended actions based on contributing factors
const generateRecommendations = (
  prediction: MLPrediction,
  factors: ContributingFactor[]
): RecommendedAction[] => {
  const recommendations: RecommendedAction[] = [];

  // Check for contact-related factors
  const daysSinceContact = factors.find((f) => f.factor.includes('days_since_last_contact'));
  if (daysSinceContact && Number(daysSinceContact.value) > 14) {
    recommendations.push({
      action: 'Prioritize outreach this week',
      priority: 'high',
      icon: <Phone className="w-4 h-4" />,
      reason: `${daysSinceContact.value} days since last contact`,
    });
  }

  // Check for engagement trend
  const engagementTrend = factors.find((f) => f.factor.includes('engagement_trend'));
  if (engagementTrend && Number(engagementTrend.value) < -10) {
    recommendations.push({
      action: 'Schedule wellness check-in',
      priority: 'high',
      icon: <Clock className="w-4 h-4" />,
      reason: 'Significant engagement decline detected',
    });
  }

  // Check for SMS success rate
  const smsSuccess = factors.find((f) => f.factor.includes('sms_success'));
  if (smsSuccess && Number(smsSuccess.value) < 0.3) {
    recommendations.push({
      action: 'Try phone call instead of SMS',
      priority: 'medium',
      icon: <Phone className="w-4 h-4" />,
      reason: 'Low SMS response rate',
    });
  }

  // Check for consecutive failures
  const consecutiveFailures = factors.find((f) => f.factor.includes('consecutive_failures'));
  if (consecutiveFailures && Number(consecutiveFailures.value) >= 3) {
    recommendations.push({
      action: 'Consider home visit or alternative contact',
      priority: 'high',
      icon: <Home className="w-4 h-4" />,
      reason: `${consecutiveFailures.value} consecutive contact failures`,
    });
  }

  // Check for SDOH factors
  const sdohRisk = factors.find((f) => f.factor.includes('sdoh') || f.factor.includes('poverty'));
  if (sdohRisk && sdohRisk.impact === 'high') {
    recommendations.push({
      action: 'Connect with community resources',
      priority: 'medium',
      icon: <Info className="w-4 h-4" />,
      reason: 'High social determinants risk in area',
    });
  }

  // Default recommendation if none generated
  if (recommendations.length === 0) {
    recommendations.push({
      action: 'Continue regular engagement cadence',
      priority: 'low',
      icon: <CheckCircle className="w-4 h-4" />,
      reason: 'No immediate intervention needed',
    });
  }

  return recommendations;
};

// Get color classes based on risk level
const getRiskColorClasses = (riskLevel: string) => {
  switch (riskLevel) {
    case 'critical':
      return {
        bg: 'bg-red-50',
        text: 'text-red-700',
        border: 'border-red-300',
        progress: 'bg-red-600',
        badge: 'bg-red-100 text-red-800 border-red-300',
      };
    case 'high':
      return {
        bg: 'bg-orange-50',
        text: 'text-orange-700',
        border: 'border-orange-300',
        progress: 'bg-orange-500',
        badge: 'bg-orange-100 text-orange-800 border-orange-300',
      };
    case 'moderate':
      return {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-300',
        progress: 'bg-blue-500',
        badge: 'bg-blue-100 text-blue-800 border-blue-300',
      };
    case 'low':
    default:
      return {
        bg: 'bg-green-50',
        text: 'text-green-700',
        border: 'border-green-300',
        progress: 'bg-green-600',
        badge: 'bg-green-100 text-green-800 border-green-300',
      };
  }
};

// Get icon based on risk level
const getRiskIcon = (riskLevel: string) => {
  switch (riskLevel) {
    case 'critical':
      return <AlertCircle className="w-4 h-4 text-red-600" />;
    case 'high':
      return <AlertTriangle className="w-4 h-4 text-orange-600" />;
    case 'moderate':
      return <Info className="w-4 h-4 text-blue-600" />;
    case 'low':
    default:
      return <CheckCircle className="w-4 h-4 text-green-600" />;
  }
};

// Format factor name for display
const formatFactorName = (factor: string): string => {
  return factor
    .replace(/^f_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

// Get impact color classes
const getImpactColorClasses = (impact: string) => {
  switch (impact) {
    case 'high':
      return 'text-red-700';
    case 'medium':
      return 'text-orange-600';
    case 'low':
    default:
      return 'text-green-700';
  }
};

// Get priority color classes
const getPriorityColorClasses = (priority: string) => {
  switch (priority) {
    case 'high':
      return 'text-red-600';
    case 'medium':
      return 'text-orange-600';
    case 'low':
    default:
      return 'text-green-600';
  }
};

export const MLPredictionInsightsPanel: React.FC<MLPredictionInsightsPanelProps> = ({
  memberId,
  enrollmentId,
  compact = false,
  onRefresh,
}) => {
  const { token } = useAuth();
  const { fmt } = useFormattedDate();
  const [loading, setLoading] = useState(true);
  const [prediction, setPrediction] = useState<MLPrediction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(!compact);

  // Fetch prediction from API
  const fetchPrediction = async () => {
    if (!memberId || !token) return;

    try {
      setLoading(true);
      setError(null);

      const url = enrollmentId
        ? getWeaveinApiUrl(`/api/ml/predictions/member/${memberId}?enrollment_id=${enrollmentId}`)
        : getWeaveinApiUrl(`/api/ml/predictions/member/${memberId}`);

      const response = await authFetch(url);

      if (response.ok) {
        const data = await response.json();
        setPrediction(data);
      } else if (response.status === 404) {
        // No prediction available from ML model
        setPrediction(null);
        setError('No ML prediction available. Run the ML model to generate predictions.');
      } else {
        throw new Error('Failed to fetch prediction');
      }
    } catch (err) {
      console.error('Error fetching ML prediction:', err);
      setError('Unable to load ML prediction. Please try again later.');
      setPrediction(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrediction();
  }, [memberId, enrollmentId, token]);

  const handleRefresh = () => {
    fetchPrediction();
    onRefresh?.();
  };

  if (loading) {
    return (
      <Card className="bg-gray-50 border-border">
        <CardContent className="flex items-center gap-2 p-4">
          <Loader2 className="w-5 h-5 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Loading AI predictions...
          </p>
        </CardContent>
      </Card>
    );
  }

  if (error && !prediction) {
    return (
      <Alert className="mb-2 border-orange-300 bg-orange-50">
        <AlertTriangle className="h-4 w-4 text-orange-600" />
        <AlertDescription className="text-orange-800">{error}</AlertDescription>
      </Alert>
    );
  }

  if (!prediction) {
    return (
      <Alert className="mb-2 border-blue-300 bg-blue-50">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          ML prediction not available for this member.
        </AlertDescription>
      </Alert>
    );
  }

  const recommendations = generateRecommendations(prediction, prediction.contributing_factors || []);
  const percentDisplay = Math.round(prediction.churn_probability * 100);
  const colorClasses = getRiskColorClasses(prediction.risk_level);
  // Interim: churn % is an engagement-based heuristic on real data, not a trained
  // model yet (the model accrues labeled history daily via the feature store).
  const isInterim = (prediction.model_version || '').startsWith('engagement_heuristic');

  return (
    <Card className="bg-gray-50 border-border">
      <CardContent className={compact ? 'p-4 pb-2' : 'p-4 pb-4'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            <h3 className="text-base font-bold text-foreground">
              AI Prediction Insights
            </h3>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={handleRefresh}
              title="Refresh prediction"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {compact && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            )}
          </div>
        </div>

        {isInterim && (
          <div className="mb-3 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] leading-snug text-amber-800">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Engagement-based estimate from live data — the predictive churn model is still
              in training (accruing labeled history). Use for directional outreach triage,
              not as a validated score.
            </span>
          </div>
        )}

        {/* Main Prediction Display */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">
              {prediction.prediction_horizon_days}-Day Churn {isInterim ? 'Estimate' : 'Probability'}
            </p>
            <Badge variant="outline" className={colorClasses.badge}>
              <span className="flex items-center gap-1">
                {getRiskIcon(prediction.risk_level)}
                {prediction.risk_level.toUpperCase()}
              </span>
            </Badge>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`absolute top-0 left-0 h-full ${colorClasses.progress} transition-all duration-300`}
                  style={{ width: `${percentDisplay}%` }}
                />
              </div>
            </div>
            <span className={`text-2xl font-bold ${colorClasses.text}`}>
              {percentDisplay}%
            </span>
          </div>
        </div>

        {expanded && (
          <>
            {/* Contributing Factors */}
            {prediction.contributing_factors && prediction.contributing_factors.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-muted-foreground font-medium mb-2 uppercase tracking-wide">
                  TOP CONTRIBUTING FACTORS
                </p>
                <ul className="space-y-1">
                  {prediction.contributing_factors.slice(0, 5).map((factor, index) => (
                    <li key={index} className="flex items-start gap-2 py-1">
                      <div className="mt-0.5">
                        {factor.impact === 'high' ? (
                          <TrendingUp className={`w-4 h-4 ${getImpactColorClasses(factor.impact)}`} />
                        ) : factor.impact === 'medium' ? (
                          <TrendingDown className={`w-4 h-4 ${getImpactColorClasses(factor.impact)}`} />
                        ) : (
                          <CheckCircle className={`w-4 h-4 ${getImpactColorClasses(factor.impact)}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-foreground">
                          {formatFactorName(factor.factor)}:{' '}
                          <strong>
                            {typeof factor.value === 'number'
                              ? factor.value % 1 === 0
                                ? factor.value
                                : factor.value.toFixed(1)
                              : factor.value}
                          </strong>
                        </p>
                        <p className={`text-xs ${getImpactColorClasses(factor.impact)}`}>
                          {factor.impact} impact ({(factor.importance * 100).toFixed(0)}% weight)
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <hr className="border-border my-3" />

            {/* Recommended Actions */}
            <div>
              <p className="text-xs text-muted-foreground font-medium mb-2 uppercase tracking-wide">
                RECOMMENDED ACTIONS
              </p>
              <ul className="space-y-1">
                {recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-2 py-1">
                    <div className={`mt-0.5 ${getPriorityColorClasses(rec.priority)}`}>
                      {rec.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${rec.priority === 'high' ? 'font-medium' : 'font-normal'} text-foreground`}>
                        {rec.action}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {rec.reason}
                      </p>
                    </div>
                    {rec.priority === 'high' && (
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300 text-xs">
                        Priority
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Model Info */}
            <div className="mt-4 pt-2 border-t border-dashed border-border">
              <p className="text-xs text-muted-foreground">
                Model: {prediction.model_version} · Predicted: {fmt.short(prediction.predicted_at)}
              </p>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MLPredictionInsightsPanel;
